import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { assignmentService, type AssignmentWithDetails, type ProgramItemWithProgress } from '../../services'
import { VideoPlayerModal } from '../video'
import { useToast } from '../common/Toast'
import { EmailStatusBadge, ResendEmailButton } from '../assignment'
import { useAuthStore } from '../../stores'
import type { AssignmentStatus } from '../../types/database.types'

interface ProgramAssignmentCardProps {
  assignment: AssignmentWithDetails
  onUpdate: () => void
}

const statusColors: Record<AssignmentStatus | 'expired', { bg: string; text: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-800' },
  expired: { bg: 'bg-red-100', text: 'text-red-800' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600' },
}

export function ProgramAssignmentCard({
  assignment,
  onUpdate,
}: ProgramAssignmentCardProps) {
  const toast = useToast()
  const { user } = useAuthStore()
  const [progressItems, setProgressItems] = useState<ProgramItemWithProgress[]>([])
  const [isLoadingProgress, setIsLoadingProgress] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<ProgramItemWithProgress['video'] | null>(null)

  // Determine actual status (check expiration)
  const isExpired =
    assignment.status === 'active' &&
    assignment.expires_at &&
    new Date(assignment.expires_at) < new Date()
  const displayStatus = isExpired ? 'expired' : assignment.status
  const statusColor = statusColors[displayStatus]

  // Load progress data on mount
  useEffect(() => {
    loadProgress()
  }, [assignment.id])

  const loadProgress = async () => {
    if (!assignment.client_id) return
    setIsLoadingProgress(true)
    try {
      const progressData = await assignmentService.getAssignmentWithProgress(
        assignment.id,
        assignment.client_id
      )
      if (progressData) {
        setProgressItems(progressData.program.items)
      }
    } catch (error) {
      console.error('Failed to load progress:', error)
    } finally {
      setIsLoadingProgress(false)
    }
  }

  const copyAccessLink = async () => {
    if (!assignment.access_token) return
    const link = assignmentService.getAssignmentAccessLink(assignment.access_token)
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Access link copied!')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  const handleComplete = async () => {
    try {
      await assignmentService.completeAssignment(assignment.id)
      toast.success('Assignment marked as complete')
      onUpdate()
    } catch {
      toast.error('Failed to complete assignment')
    }
  }

  const handleCancel = async () => {
    if (!confirm('Cancel this assignment? The client will lose access.')) {
      return
    }
    try {
      await assignmentService.cancelAssignment(assignment.id)
      toast.success('Assignment cancelled')
      onUpdate()
    } catch {
      toast.error('Failed to cancel assignment')
    }
  }

  const handleResendEmail = async () => {
    if (!assignment.client?.email) {
      toast.error('Client has no email address')
      return
    }
    try {
      const result = await assignmentService.sendAssignmentEmail(
        {
          ...assignment,
          client: {
            id: assignment.client_id,
            email: assignment.client.email,
            full_name: assignment.client.full_name,
          },
        } as any,
        assignment.program?.title || 'Exercise Program',
        user?.user_metadata?.full_name || 'Your Coach',
        progressItems.length
      )
      if (result.success) {
        toast.success('Email sent successfully')
        onUpdate()
      } else {
        toast.error(result.error || 'Failed to send email')
      }
    } catch {
      toast.error('Failed to send email')
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  const getThumbnailUrl = (video: ProgramItemWithProgress['video']): string | null => {
    if (video?.thumbnail_url) return video.thumbnail_url
    if (video?.mux_playback_id) {
      return `https://image.mux.com/${video.mux_playback_id}/thumbnail.png?width=160&height=96&fit_mode=smartcrop`
    }
    return null
  }

  const progress = assignment.progress_summary
  const progressPercent = progress?.progress_percent || 0

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Client Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-medium text-gray-600">
                {assignment.client?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/dashboard/clients/${assignment.client_id}`}
                  className="font-medium text-gray-900 hover:text-blue-600"
                >
                  {assignment.client?.full_name || 'Unknown'}
                </Link>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor.bg} ${statusColor.text}`}
                >
                  {displayStatus}
                </span>
              </div>
              <p className="text-sm text-gray-500">{assignment.client?.email}</p>
              {/* Email Status */}
              <div className="flex items-center gap-2 mt-1">
                <EmailStatusBadge
                  emailSentAt={assignment.email_sent_at}
                  emailSendCount={assignment.email_send_count || 0}
                  emailLastError={assignment.email_last_error}
                />
                {assignment.status === 'active' && !isExpired && (
                  <ResendEmailButton
                    emailSentAt={assignment.email_sent_at}
                    clientEmail={assignment.client?.email || null}
                    onResend={handleResendEmail}
                    compact
                  />
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {assignment.status === 'active' && !isExpired && (
            <div className="flex items-center gap-2">
              <button
                onClick={copyAccessLink}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy access link"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                  />
                </svg>
              </button>
              <button
                onClick={handleComplete}
                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Mark as complete"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
              <button
                onClick={handleCancel}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Cancel assignment"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Progress bar + metadata */}
        <div className="mt-3 flex items-center gap-4">
          {/* Progress bar */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    progressPercent === 100
                      ? 'bg-green-500'
                      : progressPercent > 50
                      ? 'bg-blue-500'
                      : 'bg-yellow-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700 w-10 text-right">
                {Math.round(progressPercent)}%
              </span>
            </div>
          </div>

          {/* Dates */}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>
              Assigned: {formatDate(assignment.assigned_at)}
            </span>
            {assignment.expires_at && (
              <span className={isExpired ? 'text-red-600' : ''}>
                Expires: {formatDate(assignment.expires_at)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Exercise Progress Section */}
      <div className="p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Exercise Progress
        </h4>

        {isLoadingProgress ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg
              className="animate-spin w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Loading progress...
          </div>
        ) : progressItems.length === 0 ? (
          <p className="text-sm text-gray-500">No exercise data available</p>
        ) : (
          <div className="space-y-2">
            {progressItems.map((item, index) => {
              const isCompleted = !!item.progress?.completed_at
              const watchedSeconds = item.progress?.watched_seconds || 0
              const totalSeconds = item.video?.duration_seconds || 0
              const watchPercent =
                totalSeconds > 0
                  ? Math.round((watchedSeconds / totalSeconds) * 100)
                  : 0

              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    isCompleted ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  {/* Position */}
                  <span className="w-5 h-5 flex items-center justify-center text-xs font-medium text-gray-500 bg-white rounded">
                    {index + 1}
                  </span>

                  {/* Thumbnail */}
                  <button
                    onClick={() => setSelectedVideo(item.video)}
                    className="w-12 h-8 bg-gray-200 rounded overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-blue-400"
                  >
                    {getThumbnailUrl(item.video) ? (
                      <img
                        src={getThumbnailUrl(item.video)!}
                        alt={item.video?.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </button>

                  {/* Title */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.video?.title || 'Untitled'}
                    </p>
                  </div>

                  {/* Duration */}
                  <span className="text-xs text-gray-500">
                    {formatDuration(totalSeconds)}
                  </span>

                  {/* Watched progress */}
                  <div className="flex items-center gap-1.5 w-24">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          isCompleted ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(watchPercent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-8 text-right">
                      {watchPercent}%
                    </span>
                  </div>

                  {/* Status badge */}
                  {isCompleted ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      <svg
                        className="w-3 h-3 mr-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Done
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Pending
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Notes section (if present) */}
      {assignment.notes && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <span className="font-medium">Note:</span> {assignment.notes}
          </p>
        </div>
      )}

      {/* Video preview modal */}
      <VideoPlayerModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo}
      />
    </div>
  )
}
